// Small data-fetching hook with loading/error/data states and a refetch.
import { useCallback, useEffect, useRef, useState } from "react";
import { api, ApiClientError } from "./api";

interface State<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
}

export function useApi<T>(path: string | null): State<T> & { refetch: () => void } {
  const [state, setState] = useState<State<T>>({ data: null, loading: !!path, error: null });
  const reqId = useRef(0);

  const run = useCallback(() => {
    if (!path) {
      setState({ data: null, loading: false, error: null });
      return;
    }
    const id = ++reqId.current;
    setState((s) => ({ ...s, loading: true, error: null }));
    api
      .get<T>(path)
      .then((data) => {
        if (id === reqId.current) setState({ data, loading: false, error: null });
      })
      .catch((err) => {
        if (id !== reqId.current) return;
        const message = err instanceof ApiClientError ? err.message : "Failed to load data";
        setState({ data: null, loading: false, error: message });
      });
  }, [path]);

  useEffect(() => {
    run();
  }, [run]);

  return { ...state, refetch: run };
}
